import type { Spot } from '../ScopingEditor/useGetState';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';

export const SPOT_ICON_SIZE = 1.75;

// Fixed IDs for the single shared pair of spot filters.
// All SpotItem instances reference these instead of creating per-instance <defs>.
const SPOT_FILTER_NORMAL_ID = 'spot-filter-normal';
const SPOT_FILTER_SELECTED_ID = 'spot-filter-selected';

/**
 * Renders a single shared <defs> block with the two spot color filters.
 * Mount this once inside the parent SVG so all SpotItem instances can
 * reference the filters by ID without duplicating them.
 */
export function SpotFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray */}
      <filter id={SPOT_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={SPOT_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function SpotItem({
  x = 0,
  y = 0,
  name,
  id,
  selected,
  selecting = false,
  fade = false,
  locked = false,
  isOccupied = false,
  onClick,
  ...rest
}: {
  selected: boolean;
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  isOccupied?: boolean;
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Spot, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = SPOT_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;
  const filterId = showAsSelected ? SPOT_FILTER_SELECTED_ID : SPOT_FILTER_NORMAL_ID;

  return (
    <g
      data-id="spot-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        label={name}
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {isOccupied && (
              /* Filled variant of the same icon shape — sensor3p renders on top */
              <use
                href={`${ICONS_SPRITE_URL}#icon-icon-spotfilled`}
                width={size}
                height={size}
                x={-size / 2}
                y={-size / 2}
                filter={`url(#${filterId})`}
                pointerEvents="none"
              />
            )}
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-icon-spotoutlined`}
              width={size}
              height={size}
              x={-size / 2}
              y={-size / 2}
              filter={`url(#${filterId})`}
              pointerEvents="none"
            />
            {/* sole hit target: circle sized to match the visible icon */}
            <circle cx={0} cy={0} r={size * 0.4} fill="transparent" pointerEvents="all" />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(SpotItem);
